import React from "react";
import TextArea from "../../shared/forms/TextArea";
import TextInput from "../../shared/forms/TextInput";
import SearchableDropdown from "../../shared/SearchableDropdown";

/**
 * Basic information section of the species form
 * Handles name, category, and description inputs
 * 
 * @param {Object} newSpecieData - Current form data {name, category_name, description}
 * @param {Function} setNewSpecieData - Setter for form data
 * @param {string} categorySearchQuery - Search query for category dropdown
 * @param {Function} setCategorySearchQuery - Setter for category search
 * @param {boolean} isCategoryDropdownOpen - Category dropdown open state
 * @param {Function} setIsCategoryDropdownOpen - Setter for dropdown state
 * @param {Array} filteredCategories - Filtered category list based on search
 * @param {string} speciesType - Currently selected category
 * @param {Function} handleCategorySelect - Handler for category selection
 * @param {Function} setSpeciesType - Setter for species type
 * @param {Object} categoryDropdownRef - Ref for category dropdown
 * 
 * @param {string} habitatSearchQuery
 * @param {Function} setHabitatSearchQuery
 * @param {boolean} isHabitatDropdownOpen
 * @param {Function} setIsHabitatDropdownOpen
 * @param {array} filteredHabitats
 * @param {string} speciesHabitat
 * @param {Function} handleHabitatSelect
 * @param {Function} setSpeciesHabitat
 * @param {Object} habitatDropdownRef
 */
export default function BasicInfoSection({
    newSpecieData,
    setNewSpecieData,
    categorySearchQuery,
    setCategorySearchQuery,
    isCategoryDropdownOpen,
    setIsCategoryDropdownOpen,
    filteredCategories,
    speciesType,
    handleCategorySelect,
    setSpeciesType,
    categoryDropdownRef,

    //Habitat
    habitatSearchQuery,
    setHabitatSearchQuery,
    isHabitatDropdownOpen,
    setIsHabitatDropdownOpen,
    filteredHabitats,
    speciesHabitat,
    handleHabitatSelect,
    setSpeciesHabitat,
    habitatDropdownRef,

}) {
    return (
        <>
            {/* Name Input */}
            <div>
                <label className="block text-sm font-semibold text-charcoal mb-2">
                    Artnamn *
                </label>
                <input
                    type="text"
                    placeholder="Ange artnamn"
                    value={newSpecieData.name}
                    onChange={(e) => setNewSpecieData({ ...newSpecieData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                />
            </div>

            {/* Category Select */}
            <div className="relative" ref={categoryDropdownRef}>
                <label className="block text-sm font-semibold text-charcoal mb-2">
                    Kategori *
                </label>
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Sök eller välj kategori..."
                        value={categorySearchQuery}
                        onChange={(e) => {
                            setCategorySearchQuery(e.target.value);
                            setIsCategoryDropdownOpen(true);
                        }}
                        onFocus={() => setIsCategoryDropdownOpen(true)}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                    />
                    <button
                        type="button"
                        onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                    >
                        <svg 
                            className={`w-5 h-5 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {/* Dropdown List */}
                    {isCategoryDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {filteredCategories.length > 0 ? (
                                <>
                                    <div 
                                        onClick={() => {
                                            setSpeciesType("");
                                            setCategorySearchQuery("");
                                            setIsCategoryDropdownOpen(false);
                                            setNewSpecieData((prevState) => ({
                                                ...prevState,
                                                category_name: "",
                                            }));
                                        }}
                                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-500 italic border-b border-gray-200"
                                    >
                                        Ingen kategori vald
                                    </div>
                                    {filteredCategories.map((category) => (
                                        <div
                                            key={category}
                                            onClick={() => handleCategorySelect(category)}
                                            className={`px-4 py-2 hover:bg-sage cursor-pointer transition-colors ${
                                                speciesType === category ? 'bg-sage font-semibold' : ''
                                            }`}
                                        >
                                            {category}
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <div className="px-4 py-3 text-gray-500 text-center">
                                    Ingen kategori hittades
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Habitat Select */}
            <div className="relative" ref={habitatDropdownRef}>
                <label className="block text-sm font-semibold text-charcoal mb-2">
                    Habitat
                </label>
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Sök eller välj habitat..."
                        value={habitatSearchQuery}
                        onChange={(e) => {
                            setHabitatSearchQuery(e.target.value);
                            setIsHabitatDropdownOpen(true);
                        }}
                        onFocus={() => setIsHabitatDropdownOpen(true)}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
                    />
                    <button
                        type="button"
                        onClick={() => setIsHabitatDropdownOpen(!isHabitatDropdownOpen)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                    >
                        <svg 
                            className={`w-5 h-5 transition-transform ${isHabitatDropdownOpen ? 'rotate-180' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {/* Dropdown List */}
                    {isHabitatDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {filteredHabitats.length > 0 ? (
                                <>
                                    <div 
                                        onClick={() => {
                                            setSpeciesHabitat("");
                                            setHabitatSearchQuery("");
                                            setIsHabitatDropdownOpen(false);
                                            setNewSpecieData((prevState) => ({
                                                ...prevState,
                                                habitat: "",
                                            }));
                                        }}
                                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-500 italic border-b border-gray-200"
                                    >
                                        Inget valt habitat
                                    </div>
                                    {filteredHabitats.map((habitat) => (
                                        <div
                                            key={habitat}
                                            onClick={() => handleHabitatSelect(habitat)}
                                            className={`px-4 py-2 hover:bg-sage cursor-pointer transition-colors ${
                                                speciesHabitat === habitat ? 'bg-sage font-semibold' : ''
                                            }`}
                                        >
                                            {habitat}
                                        </div>
                                    ))}
                                </>
                            ) : (
                                <div className="px-4 py-3 text-gray-500 text-center">
                                    Inget habitat hittades
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Description Textarea */}
            <TextArea
                label="Beskrivning"
                placeholder="Beskrivning av arten"
                value={newSpecieData.description}
                onChange={(e) => setNewSpecieData({ ...newSpecieData, description: e.target.value })}
                rows={4}
            />
        </>
    );
}
